import { useState, useCallback } from 'react';
import {
  TableState,
  SortState,
  ColumnFilter,
  ColumnVisibilityState,
  RowSelection,
  TableQueryParams,
  DataTableColumn,
} from 'types/table.types';
import {
  saveColumnVisibility,
  loadColumnVisibility,
  saveColumnOrder,
  loadColumnOrder,
  getDefaultColumnVisibility,
  convertSortingToBackendFormat,
} from 'utils/tableHelpers';

export interface UseTableStateOptions {
  defaultPageSize?: number;
  enableColumnVisibility?: boolean;
  enableColumnReorder?: boolean;
  columnVisibilityStorageKey?: string;
  columnOrderStorageKey?: string;
  columns?: DataTableColumn<any>[];
}

export const useTableState = (options: UseTableStateOptions = {}) => {
  const {
    defaultPageSize = 20,
    enableColumnVisibility = true,
    enableColumnReorder = true,
    columnVisibilityStorageKey = 'table_visibility',
    columnOrderStorageKey = 'table_order',
    columns = [],
  } = options;

  // Main table state
  const [state, setState] = useState<TableState>({
    pagination: {
      pageNumber: 1,
      pageSize: defaultPageSize,
    },
    sorting: [],
    columnFilters: [],
    globalFilter: '',
    columnVisibility: enableColumnVisibility
      ? getDefaultColumnVisibility(columns, columnVisibilityStorageKey)
      : columns.reduce((acc, col) => {
          const key = (col.accessorKey as string) || (col.id as string) || '';
          acc[key] = true;
          return acc;
        }, {} as ColumnVisibilityState),
    rowSelection: {},
  });

  // Column Order State (separate to avoid name conflict with setter function)
  const [columnOrder, setColumnOrderState] = useState<string[]>(() => {
    if (!enableColumnReorder) {
      return columns.map((col) => (col.accessorKey as string) || (col.id as string) || '');
    }
    return (
      loadColumnOrder(columnOrderStorageKey) ||
      columns.map((col) => (col.accessorKey as string) || (col.id as string) || '')
    );
  });

  // ====================== Handlers ======================

  // Pagination
  const setPaginationState = useCallback(
    (pagination: Partial<{ pageNumber: number; pageSize: number }>) => {
      setState((prev) => ({
        ...prev,
        pagination: { ...prev.pagination, ...pagination },
      }));
    },
    [],
  );

  const goToPage = useCallback(
    (pageNumber: number) => setPaginationState({ pageNumber }),
    [setPaginationState],
  );

  const setPageSize = useCallback(
    (pageSize: number) => {
      setPaginationState({ pageSize, pageNumber: 1 });
    },
    [setPaginationState],
  );

  const nextPage = useCallback(() => {
    setState((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        pageNumber: prev.pagination.pageNumber + 1,
      },
    }));
  }, []);

  const previousPage = useCallback(() => {
    setState((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        pageNumber: Math.max(1, prev.pagination.pageNumber - 1),
      },
    }));
  }, []);

  // Sorting
  const setSortingState = useCallback((sorting: SortState[]) => {
    setState((prev) => ({
      ...prev,
      sorting,
      pagination: { ...prev.pagination, pageNumber: 1 },
    }));
  }, []);

  // Filtering
  const setColumnFilters = useCallback((filters: ColumnFilter[]) => {
    setState((prev) => ({
      ...prev,
      columnFilters: filters,
      pagination: { ...prev.pagination, pageNumber: 1 },
    }));
  }, []);

  const addColumnFilter = useCallback((filter: ColumnFilter) => {
    setState((prev) => {
      const existingIndex = prev.columnFilters.findIndex((f) => f.id === filter.id);
      const newFilters =
        existingIndex >= 0
          ? [
              ...prev.columnFilters.slice(0, existingIndex),
              filter,
              ...prev.columnFilters.slice(existingIndex + 1),
            ]
          : [...prev.columnFilters, filter];

      return {
        ...prev,
        columnFilters: newFilters,
        pagination: { ...prev.pagination, pageNumber: 1 },
      };
    });
  }, []);

  const removeColumnFilter = useCallback((filterId: string) => {
    setState((prev) => ({
      ...prev,
      columnFilters: prev.columnFilters.filter((f) => f.id !== filterId),
      pagination: { ...prev.pagination, pageNumber: 1 },
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setState((prev) => ({
      ...prev,
      columnFilters: [],
      globalFilter: '',
      pagination: { ...prev.pagination, pageNumber: 1 },
    }));
  }, []);

  const setGlobalFilter = useCallback((search: string) => {
    setState((prev) => ({
      ...prev,
      globalFilter: search,
      pagination: { ...prev.pagination, pageNumber: 1 },
    }));
  }, []);

  // Column Visibility
  const setColumnVisibility = useCallback(
    (visibility: ColumnVisibilityState) => {
      setState((prev) => ({ ...prev, columnVisibility: visibility }));

      if (enableColumnVisibility) {
        saveColumnVisibility(columnVisibilityStorageKey, visibility);
      }
    },
    [enableColumnVisibility, columnVisibilityStorageKey],
  );

  const toggleColumnVisibility = useCallback((columnId: string) => {
    setState((prev) => ({
      ...prev,
      columnVisibility: {
        ...prev.columnVisibility,
        [columnId]: !prev.columnVisibility[columnId],
      },
    }));
  }, []);

  // Column Reordering
  const setColumnOrder = useCallback(
    (order: string[]) => {
      setColumnOrderState(order);
      if (enableColumnReorder) {
        saveColumnOrder(columnOrderStorageKey, order);
      }
    },
    [enableColumnReorder, columnOrderStorageKey],
  );

  // Row Selection
  const setRowSelection = useCallback((selection: RowSelection) => {
    setState((prev) => ({ ...prev, rowSelection: selection }));
  }, []);

  const toggleRowSelection = useCallback((rowId: string) => {
    setState((prev) => ({
      ...prev,
      rowSelection: {
        ...prev.rowSelection,
        [rowId]: !prev.rowSelection[rowId],
      },
    }));
  }, []);

  const selectAllRows = useCallback(
    (rowIds: string[]) => {
      const selection = rowIds.reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {} as RowSelection);
      setRowSelection(selection);
    },
    [setRowSelection],
  );

  const deselectAllRows = useCallback(() => {
    setRowSelection({});
  }, [setRowSelection]);

  // Build query parameters for API (Fixed type issue)
  const buildQueryParams = useCallback((): TableQueryParams => {
    const { sortBy, sortDirection } = convertSortingToBackendFormat(state.sorting);

    return {
      pageNumber: state.pagination.pageNumber,
      pageSize: state.pagination.pageSize,
      search: state.globalFilter || undefined,
      sortBy,
      // Type-safe narrowing for sortDirection
      sortDirection:
        sortDirection === 'asc' || sortDirection === 'desc' ? sortDirection : undefined,
      // Column-specific filters
      ...state.columnFilters.reduce((acc, filter) => {
        if (filter.value !== undefined && filter.value !== null && filter.value !== '') {
          acc[filter.id] = filter.value;
        }
        return acc;
      }, {} as Record<string, any>),
    };
  }, [state]);

  // Reset entire state
  const reset = useCallback(() => {
    setState({
      pagination: {
        pageNumber: 1,
        pageSize: defaultPageSize,
      },
      sorting: [],
      columnFilters: [],
      globalFilter: '',
      columnVisibility: getDefaultColumnVisibility(columns, columnVisibilityStorageKey),
      rowSelection: {},
    });
  }, [defaultPageSize, columns, columnVisibilityStorageKey]);

  return {
    // State
    state,
    columnOrder,

    // Pagination
    goToPage,
    setPageSize,
    nextPage,
    previousPage,

    // Sorting
    setSortingState,

    // Filtering & Search
    setColumnFilters,
    addColumnFilter,
    removeColumnFilter,
    clearFilters,
    setGlobalFilter,

    // Column visibility
    setColumnVisibility,
    toggleColumnVisibility,

    // Column reordering
    setColumnOrder,

    // Row selection
    setRowSelection,
    toggleRowSelection,
    selectAllRows,
    deselectAllRows,

    // Utils
    buildQueryParams,
    reset,
  };
};
